import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiSearch, FiX } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import ErrorBoundary from '../components/ErrorBoundary';
import { expandHexColor, isHexColor } from '../utils/redirectGroupColor';

const emptyForm = {
    groupName: '',
    color: '#3B82F6',
};

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'ngrok-skip-browser-warning': 'true',
});

const inputClass =
    'mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-3';

const RedirectGroupsPageContent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState(emptyForm);
    const [selected, setSelected] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showView, setShowView] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchItems = async (page, search = '') => {
        try {
            setLoading(true);
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/redirect-groups?page=${page}&limit=20`;
            if (search) apiUrl += `&search=${encodeURIComponent(search)}`;
            const response = await axios.get(apiUrl, { headers: authHeaders() });
            if (response.data.success) {
                setItems(response.data.data);
                setTotalPages(response.data.pagination.pages);
                setTotalItems(response.data.pagination.total);
            }
        } catch (err) {
            setError(`Failed to fetch groups: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems(currentPage, searchTerm);
    }, [currentPage, searchTerm]);

    useEffect(() => {
        if (location.state?.openCreate) {
            setFormData(emptyForm);
            setShowCreate(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    const closeModals = () => {
        setShowCreate(false);
        setShowEdit(false);
        setShowView(false);
        setShowDelete(false);
        setSelected(null);
        setFormData(emptyForm);
    };

    const fillForm = (row) => {
        setFormData({
            groupName: row.groupName || '',
            color: expandHexColor(row.color),
        });
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/redirect-groups`,
                formData,
                { headers: authHeaders() }
            );
            closeModals();
            fetchItems(currentPage, searchTerm);
        } catch (err) {
            setError(`Failed to create: ${err.response?.data?.error || err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        if (!selected) return;
        try {
            setSaving(true);
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/redirect-groups/${selected._id}`,
                formData,
                { headers: authHeaders() }
            );
            closeModals();
            fetchItems(currentPage, searchTerm);
        } catch (err) {
            setError(`Failed to update: ${err.response?.data?.error || err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selected) return;
        try {
            setSaving(true);
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/redirect-groups/${selected._id}`,
                { headers: authHeaders() }
            );
            closeModals();
            fetchItems(currentPage, searchTerm);
        } catch (err) {
            setError(`Failed to delete: ${err.response?.data?.error || err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const setColor = (raw) => {
        const value = String(raw || '').trim();
        setFormData((prev) => ({ ...prev, color: value.startsWith('#') || value === '' ? value : `#${value}` }));
    };

    const formFields = (
        <>
            <div>
                <label className="block text-sm font-medium text-gray-700">Group name</label>
                <input
                    type="text"
                    required
                    className={inputClass}
                    placeholder="SLG"
                    value={formData.groupName}
                    onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Color</label>
                <div className="mt-1 flex items-center gap-2">
                    <input
                        type="color"
                        className="h-10 w-12 cursor-pointer rounded border border-gray-300 bg-white p-0"
                        value={expandHexColor(formData.color)}
                        onChange={(e) => setColor(e.target.value)}
                        aria-label="Color selector"
                    />
                    <input
                        type="text"
                        className={inputClass + ' mt-0'}
                        placeholder="#3B82F6"
                        value={formData.color}
                        onChange={(e) => setColor(e.target.value)}
                    />
                </div>
                <p className="mt-1 text-xs text-gray-500">Pick a color or enter HEX (#RGB or #RRGGBB)</p>
            </div>
        </>
    );

    return (
        <div className="space-y-6 w-full max-w-none px-8 py-6">
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <h2 className="text-xl font-semibold">Redirect Groups</h2>
                    <button
                        type="button"
                        onClick={() => {
                            setFormData(emptyForm);
                            setShowCreate(true);
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                    >
                        <FiPlus /> Add group
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md flex items-start justify-between gap-2">
                        <span>{error}</span>
                        <button type="button" onClick={() => setError('')} aria-label="Dismiss">
                            <FiX />
                        </button>
                    </div>
                )}

                <div className="flex flex-wrap gap-3 mb-4">
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            className="rounded-md border border-gray-300 py-2 px-3"
                            placeholder="Search group name"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setSearchTerm(searchInput);
                                    setCurrentPage(1);
                                }
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                setSearchTerm(searchInput);
                                setCurrentPage(1);
                            }}
                            className="p-2 rounded-md bg-gray-100 hover:bg-gray-200"
                        >
                            <FiSearch />
                        </button>
                    </div>
                    <span className="self-center text-sm text-gray-500">{totalItems} groups</span>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Color</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domains</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {items.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                                            No redirect groups
                                        </td>
                                    </tr>
                                ) : (
                                    items.map((row) => (
                                        <tr
                                            key={row._id}
                                            style={{ backgroundColor: `${expandHexColor(row.color)}33` }}
                                        >
                                            <td className="px-4 py-3 font-medium text-gray-900">{row.groupName}</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-2 text-sm text-gray-700">
                                                    <span
                                                        className="inline-block h-4 w-4 rounded border border-gray-300"
                                                        style={{ backgroundColor: expandHexColor(row.color) }}
                                                    />
                                                    {expandHexColor(row.color)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                {(row.redirectDomains || []).length
                                                    ? (row.redirectDomains || []).map((d) => d.domain || d).join(', ')
                                                    : '—'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        type="button"
                                                        title="View"
                                                        onClick={() => {
                                                            setSelected(row);
                                                            setShowView(true);
                                                        }}
                                                    >
                                                        <FiEye className="text-gray-600" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Edit"
                                                        onClick={() => {
                                                            setSelected(row);
                                                            fillForm(row);
                                                            setShowEdit(true);
                                                        }}
                                                    >
                                                        <FiEdit2 className="text-blue-600" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Delete"
                                                        onClick={() => {
                                                            setSelected(row);
                                                            setShowDelete(true);
                                                        }}
                                                    >
                                                        <FiTrash2 className="text-red-600" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                )}
            </div>

            {(showCreate || showEdit) && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">
                            {showCreate ? 'Add redirect group' : 'Edit redirect group'}
                        </h3>
                        <form onSubmit={showCreate ? handleCreate : handleEdit} className="space-y-4">
                            {formFields}
                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={closeModals} className="px-4 py-2 rounded-md border">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving || !isHexColor(formData.color)}
                                    className="px-4 py-2 rounded-md bg-blue-600 text-white disabled:opacity-50"
                                >
                                    {saving ? 'Saving…' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showView && selected && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-3">
                        <h3 className="text-lg font-semibold">Redirect group</h3>
                        <p><span className="text-gray-500">Name:</span> {selected.groupName}</p>
                        <p className="flex items-center gap-2">
                            <span className="text-gray-500">Color:</span>
                            <span
                                className="inline-block h-4 w-4 rounded border border-gray-300"
                                style={{ backgroundColor: expandHexColor(selected.color) }}
                            />
                            {expandHexColor(selected.color)}
                        </p>
                        <div>
                            <p className="text-gray-500">Domains</p>
                            {(selected.redirectDomains || []).length ? (
                                <ul className="mt-1 list-disc pl-5 text-gray-800">
                                    {(selected.redirectDomains || []).map((d) => (
                                        <li key={d._id || d}>{d.domain || d}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p>—</p>
                            )}
                        </div>
                        <div className="flex justify-end">
                            <button type="button" onClick={closeModals} className="px-4 py-2 rounded-md border">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDelete && selected && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold mb-2">Delete redirect group?</h3>
                        <p className="text-gray-600 mb-6">
                            This removes group <span className="font-medium">{selected.groupName}</span>.
                            Domains are not deleted.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={closeModals} className="px-4 py-2 rounded-md border">
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={saving}
                                onClick={handleDelete}
                                className="px-4 py-2 rounded-md bg-red-600 text-white disabled:opacity-50"
                            >
                                {saving ? 'Deleting…' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const RedirectGroupsPage = () => (
    <Layout>
        <ErrorBoundary
            fallbackTitle="Redirect Groups Error"
            fallbackDescription="An error occurred while loading redirect groups."
        >
            <RedirectGroupsPageContent />
        </ErrorBoundary>
    </Layout>
);

export default RedirectGroupsPage;
